import type { Reference } from "@apollo/client";

/**
 * `evict` means the variant's filter now matches, so it may be missing slides and must
 * be refetched; `replace` means it no longer matches and the changed slides drop out.
 */
export type FilteredListDecision =
  | { action: "keep" }
  | { action: "evict" }
  | { action: "replace"; items: readonly Reference[] };

/**
 * Apollo updates the field by id but cannot decide list membership, which is server-side:
 * it neither drops a slide from a filter it stopped matching nor adds it to one it now
 * matches. Unfiltered views need no help — the field update plus client-side grouping
 * already moves the slide. Kept free of Apollo cache types so every field hook can share
 * it, with the `DELETE` sentinel staying in the typed wrapper at each call site.
 */
export function reconcileFilteredSlideList(
  storeFieldName: string,
  fieldName: string,
  // A list even for single-valued fields, since a slide can hold several report dimensions.
  newValues: readonly string[],
  changedSlideIds: Set<string>,
  items: readonly Reference[],
  readField: (fieldName: string, from: Reference) => unknown
): FilteredListDecision {
  const filterMatch = storeFieldName.match(new RegExp(`"${fieldName}":"(\\w+)"`));
  if (!filterMatch) return { action: "keep" };

  // The slide now belongs here; we can't insert it at the right paginated/sorted
  // position, so evict and let the server repopulate.
  if (newValues.includes(filterMatch[1] as string)) return { action: "evict" };

  const filteredItems = items.filter((slideRef) => {
    const slideId = readField("id", slideRef) as string | undefined;
    return slideId ? !changedSlideIds.has(slideId) : true;
  });

  if (filteredItems.length === items.length) return { action: "keep" };
  return { action: "replace", items: filteredItems };
}
